import { useRef, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import { discoArrayToProductImageStruct, getProductImageAltText } from "helpers/product-image-helpers";
import { PdpImageZoomModalName, analytics, flagsAndExperimentNames } from "rtr-constants";
import Header from "./header";
import PDPBreadCrumbs from "../products/pdp-breadcrumbs";
import PDPImagery from "../pdp/pdp-imagery";
import PdpImageZoomModal from "../products/pdp-image-zoom-modal.jsx";
import ReviewModal from "./review-modal";
import ProductDetails from "../products/product-details";
import ProductImageRotation from "helpers/product-image-rotation";
import ProductInfo from "../products/product-info/product-info";
import ReviewThumbNailCarousel from "./review-thumbnail/review-thumbnail-carousel";
import AnchorProductAggregateRating from "components/source/products/anchor-product-aggregate-rating";
import sharedActions from "actions/shared-actions";
import useSailthruConfig from "./useSailthruConfig";
import PDPInlineReviews from "../reviews/pdp-inline-reviews";
import { MODAL_NAMES } from "constants/modal-names";
import { setPDPBuyBoxMode } from "slices/pdp-slice";
import PlansDrawerModal from "../acquisition/plans_drawer/plans-drawer-modal";
import MembershipBuyBox from "./membership-buy-box";
import ClearanceBuyBox from "./clearance-buy-box";
import ReserveBuyBox from "./reserve-buy-box";
import maternityHelper from "helpers/maternity-helper";
import PdpCarousels from "../pdp/pdp-carousels";
import { Flags } from "../hoc/with-feature-flags";
import MembershipUpgradesHelpers from "helpers/membership-upgrades-helpers";
import { isAdmin } from "components/source/hoc/with-user-data";
import PDPAdminDetail from "components/source/products/pdp-admin-detail.tsx";
import ComingSoonPDPContent from "components/source/products/coming-soon-pdp-content";
import { isActiveMembership } from "helpers/membership-helpers";
import PromoExclusion from "components/source/products/promo-exclusion";
import ProductsActions from "actions/products-actions";
import { useLoadThumbnailPhotos } from "./helpers/review-images-helper";
import { shouldExcludePromo } from "helpers/product-drawer-helper";
import { useAvailabilityFilters } from "hooks/use-availability-filters";

export default function PDP() {
  useSailthruConfig();
  const [featureImageSelected, setFeatureImageSelected] = useState(null);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewModalIndex, setReviewModalIndex] = useState(0);
  // Note: State used to render the create short list button for a 'Coming Soon' item
  const [showComingSoonShortList, setShowComingSoonShortList] = useState(false);
  const inlineReviewsRef = useRef(null);
  const isBuyNow = false;

  const userData = useSelector(state => state.userData);

  const staticProduct = useSelector(state => state.staticProduct);
  const isMobileViewport = useSelector(state => state.browser?.isMobileViewport);
  const workingFilters = useSelector(state => state.workingFilters) || {};
  const momentsWithCount = useSelector(state => state.reviewsThumbnailPhotos);
  const gridProductDrawer = useSelector(state => state.gridProductDrawer);
  const membershipState = useSelector(state => state.membershipState);
  const ceBagCount = useSelector(state => state.ceBagCount);
  const buyBoxMode = useSelector(state => state.pdpBuyBoxMode);

  const reviewStarRating = useSelector(state => state.reviews?.averageRating ?? 0);
  const numOfReviews = useSelector(state => state.reviews?.count ?? 0);
  const unlimitedAddOnFlag = useSelector(state => state.featureFlags?.[Flags.UNLIMITED_ALWAYS_ON_ADD_ONS]);
  const promoExclusionFlag = useSelector(
    state => state.flagsAndExperiments?.[flagsAndExperimentNames.CATALOG_SERVICES_SHOW_PROMO_EXCLUSION]
  );

  const reviewsThumbnailPhotos = momentsWithCount?.moments;
  const dispatch = useDispatch();

  const { setOrToggleMembershipAvailability } = useAvailabilityFilters(() => {});
  useLoadThumbnailPhotos(staticProduct.productId);

  // TODO: Migrate lens logic to date filter logic
  useEffect(() => {
    dispatch(ProductsActions.setMembershipLensOnPageLoad(userData));
  }, []);

  useEffect(() => {
    if (staticProduct.merchandisingCategory === "Clearance") {
      dispatch(setPDPBuyBoxMode("clearance"));
    } else if (workingFilters?.date) {
      dispatch(setPDPBuyBoxMode("reserve"));
    } else {
      dispatch(setPDPBuyBoxMode("membership"));
    }
  }, [workingFilters.date, staticProduct.merchandisingCategory]);

  const showMaternityBumpImage = maternityHelper.showMaternityBumpImage(userData);

  // FIXME: setting maternity flag to false for now
  const imageRotation = ProductImageRotation(
    workingFilters?.canonicalSizes || [],
    staticProduct,
    showMaternityBumpImage
  );
  const altText = getProductImageAltText(staticProduct.displayName, staticProduct.designerDisplayName);
  const images = discoArrayToProductImageStruct(staticProduct.images);
  const displayedModal = useSelector(state => state.displayedModal);

  const isMember = isActiveMembership(membershipState);
  const isAccessory = Array.isArray(staticProduct?.skus) && staticProduct.skus.length === 1;

  const annotationSuffix = MembershipUpgradesHelpers.getUpgradeAnnotationAppendCE(
    membershipState,
    userData,
    ceBagCount,
    unlimitedAddOnFlag
  );

  const excludePromo = shouldExcludePromo({
    product: staticProduct,
    promoExclusionFlag,
    expectedMerchandisingCategory: "Clearance",
  });

  return (
    <div className="pdp-content">
      <div data-test-id="universal_pdp" className="pdp-main-section media-query-layout">
        <PDPImagery
          altText={altText}
          className={"product-images"}
          isCloudZoomEnabled={false}
          images={images}
          imageRotation={imageRotation}
          isMobileViewport={isMobileViewport}
          onClick={(index, ref) => {
            setFeatureImageSelected({ index, ref });
            dispatch(sharedActions.displayModal(PdpImageZoomModalName));
          }}
        />
        <div className="pdp-dynamic-section">
          <Header showComingSoonShortList={showComingSoonShortList} />
          <PromoExclusion shouldExclude={excludePromo} />
          {staticProduct?.isComingSoon && (
            <ComingSoonPDPContent
              location={analytics.ACTION_LOCATIONS.PDP}
              product={staticProduct}
              setShowComingSoonShortList={setShowComingSoonShortList}
            />
          )}
          <div className="mt-8 md:mt-6" data-test-id="review-stars-count">
            <AnchorProductAggregateRating
              productId={staticProduct.productId}
              reviews={{
                count: numOfReviews,
                averageRating: reviewStarRating,
              }}
              redirectUrl={gridProductDrawer?.pdpUrl}
            />
            <ReviewThumbNailCarousel
              setReviewModalOpen={setReviewModalOpen}
              setReviewModalIndex={setReviewModalIndex}
              productId={staticProduct.productId}
            />
          </div>
          {!staticProduct?.isComingSoon && (
            <>
              <hr className="border-offWhite mt-6"></hr>

              {(buyBoxMode === "membership" || buyBoxMode === "membership_buy") && <MembershipBuyBox />}
              {buyBoxMode === "clearance" && <ClearanceBuyBox />}
              {buyBoxMode === "reserve" && <ReserveBuyBox />}
            </>
          )}

          <div className="mt-4">
            {buyBoxMode === "membership" && (
              <p>
                {/* Changing the mode to reserve, but won't change lens until user actually selects a date */}
                <button
                  className="semibold underline"
                  onClick={() => {
                    dispatch(setPDPBuyBoxMode("reserve"));
                  }}>
                  {"Reserve in Advance"}
                </button>
                {" (members get 25% off)"}
              </p>
            )}
            {buyBoxMode === "reserve" && (
              <p>
                {isMember ? "Rent this with your membership. " : ""}
                {/* Changes lens to membership, clears date filters so mode changes to membership */}
                <button
                  className="semibold underline"
                  onClick={() => {
                    setOrToggleMembershipAvailability();
                    if (!workingFilters?.date) {
                      dispatch(setPDPBuyBoxMode("membership"));
                    }
                  }}>
                  {isMember ? "Check Availability" : "Try Subscription"}
                </button>
              </p>
            )}
          </div>
        </div>

        <PdpImageZoomModal
          altText={altText}
          imageRotation={imageRotation}
          images={images}
          isMobileViewport={isMobileViewport}
          openingElement={featureImageSelected}
        />
      </div>

      <ProductInfo
        selected={null}
        showOptionalDetails={isBuyNow}
        isMobileViewport={isMobileViewport}
        imageRotation={imageRotation}
        images={images}
        displayName={staticProduct.displayName}
        designerDisplayName={staticProduct.designerDisplayName}
        productId={staticProduct.productId}
        fitNotes={staticProduct.fitNotes}
        stylistNotes={staticProduct.stylistNotes}
        productDetails={staticProduct.productDetails}
      />

      {!isMobileViewport && (
        <ProductDetails
          selected={null}
          showOptionalDetails={isBuyNow}
          isMobileViewport={isMobileViewport}
          imageRotation={imageRotation}
          images={images}
          displayName={staticProduct.displayName}
          designerDisplayName={staticProduct.designerDisplayName}
          productId={staticProduct.productId}
          fitNotes={staticProduct.fitNotes}
          stylistNotes={staticProduct.stylistNotes}
          productDetails={staticProduct.productDetails}
        />
      )}

      <PdpCarousels
        annotationSuffix={annotationSuffix}
        dispatch={dispatch}
        isRentable={staticProduct.merchandisingCategory === "Rental"}
        isAccessory={isAccessory}
        isUnavailableToRent={false}
        isUPDP={true}
      />
      {!isMobileViewport && <div data-test-id="desktop-reviews-partial-anchor" id="reviews-partial" />}
      {!isMobileViewport && (
        <PDPInlineReviews
          dispatch={dispatch}
          key="0"
          ref={inlineReviewsRef}
          product={staticProduct}
          shouldLoadReviews={true}
          writingReview={false} // @TODO: Ensure writingReview works as in PDP - did some digging but couldn't find where
          // this value is set in legacy PDP (seems to be present in ruby)
        />
      )}

      <PDPBreadCrumbs />
      {reviewsThumbnailPhotos?.length > 0 && reviewModalOpen && (
        <ReviewModal
          productDisplayName={staticProduct?.displayName}
          productId={staticProduct?.productId}
          ind={reviewModalIndex}
          setIsOpen={setReviewModalOpen}
          isOpen={reviewModalOpen}
        />
      )}
      {isAdmin(userData) && (
        <PDPAdminDetail id={staticProduct?.productId} published={staticProduct?.stage === "PUBLISHED"} />
      )}
      <PlansDrawerModal isOpen={displayedModal === MODAL_NAMES.PLANS_DRAWER} />
    </div>
  );
}
